import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = '/api';
const POLL_MS = 30000;

const MessagesContext = createContext({ unreadCount: 0, showMessages: false, refresh: () => {} });

export const MessagesProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const cancelRef = useRef(false);

  const fetch = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      setShowMessages(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/messages/visibility-check`);
      if (!cancelRef.current) {
        setUnreadCount(data.unread_count || 0);
        setShowMessages(!!data.show_messages);
      }
    } catch {
      // silently keep previous values on transient errors
    }
  }, [user]);

  // Fetch on user change and set up 30s interval
  useEffect(() => {
    cancelRef.current = false;
    if (!user) {
      setUnreadCount(0);
      setShowMessages(false);
      return;
    }
    fetch();
    const timer = setInterval(fetch, POLL_MS);
    return () => {
      cancelRef.current = true;
      clearInterval(timer);
    };
  }, [user, fetch]);

  // Listen for imperative refresh events (e.g. after reading a message)
  useEffect(() => {
    const handler = () => fetch();
    window.addEventListener('refreshUnreadCount', handler);
    return () => window.removeEventListener('refreshUnreadCount', handler);
  }, [fetch]);

  return (
    <MessagesContext.Provider value={{ unreadCount, showMessages, refresh: fetch }}>
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => useContext(MessagesContext);
